/**
 * <composition-form>: PASTE A COMPOSITION in the bag drawer and on the bag page
 * (sections/bag-drawer.liquid, sections/cart.liquid). A pasted code
 * (assets/composition.js) is decoded, turned into the build's cart lines and
 * placed in the bag. The consultation's data — catalog, paper/ruling blocks,
 * variant maps — is fetched once per page from the Moonphase product page
 * (`data-source-url`) and read from its <moonphase-consultation> element. The
 * page is fetched whole because the consultation sits in a JSON template, whose
 * Section Rendering id (`template--<id>__main`) Liquid cannot learn from here.
 *
 * The drawer's copy is re-created by cart.js after every cart change, so
 * everything is set up in connectedCallback and nothing listens on document.
 * With `data-reload` (the bag page) a successful add reloads the page instead
 * of opening the drawer. Ordering off: the button is rendered aria-disabled and
 * submit does nothing; cart.js refuses as well.
 *
 * A composition whose variant has sold out fails with the sold-out label; when
 * the register-interest page is set (`data-sold-out-url`) the
 * `[data-composition-interest]` link then appears under the error, pointing at
 * that page with the composition appended.
 */

import { addLines } from "@theme/cart";
import { decode, encode, linesFor, readConsultationData, registerInterestUrl } from "@theme/composition";

const LOAD_ERROR = "The catalog could not be loaded. Please try again.";
const CART_ERROR = "The bag could not be updated. Please try again.";

const dataPromises = new Map();

/** The consultation's data, fetched once per page per source URL; a failed fetch may be retried. */
function loadData(sourceUrl) {
  const url = new URL(sourceUrl, window.location.origin);
  const key = url.href;
  if (!dataPromises.has(key)) {
    const promise = fetch(url, { headers: { Accept: "text/html" } })
      .then((response) => (response.ok ? response.text() : Promise.reject(new Error(LOAD_ERROR))))
      .then((html) => {
        const root = new DOMParser().parseFromString(html, "text/html").querySelector("moonphase-consultation");
        const data = root ? readConsultationData(root) : null;
        if (!data) throw new Error(LOAD_ERROR);
        return data;
      });
    promise.catch(() => dataPromises.delete(key));
    dataPromises.set(key, promise);
  }
  return dataPromises.get(key);
}

class CompositionForm extends HTMLElement {
  #busy = false;
  #gated = false;

  connectedCallback() {
    this.input = this.querySelector("[data-composition-input]");
    this.button = this.querySelector("[data-composition-submit]");
    this.error = this.querySelector("[data-composition-error]");
    this.interest = this.querySelector("[data-composition-interest]");
    if (!this.input || !this.button || !this.error) return;
    this.#gated = this.button.getAttribute("aria-disabled") === "true";
    this.button.addEventListener("click", this.#submit);
    this.input.addEventListener("keydown", this.#onKeydown);
    this.input.addEventListener("input", this.#clearError);
  }

  disconnectedCallback() {
    this.button?.removeEventListener("click", this.#submit);
    this.input?.removeEventListener("keydown", this.#onKeydown);
    this.input?.removeEventListener("input", this.#clearError);
  }

  get #orderingEnabled() {
    return !this.#gated && document.documentElement.dataset.orderingEnabled === "true";
  }

  #onKeydown = (event) => {
    if (event.key !== "Enter") return;
    event.preventDefault();
    this.#submit();
  };

  #clearError = () => {
    this.#showError("");
  };

  /** Shows an error line; with `code`, the register-interest link for that composition follows it. */
  #showError(message, code = null) {
    this.error.textContent = message;
    this.input.classList.toggle("is-error", Boolean(message));
    if (!this.interest) return;
    const url = this.dataset.soldOutUrl;
    this.interest.hidden = !(code && url);
    if (code && url) this.interest.setAttribute("href", registerInterestUrl(url, code));
  }

  #setBusy(busy) {
    this.#busy = busy;
    if (busy) {
      this.button.setAttribute("aria-busy", "true");
      this.button.setAttribute("aria-disabled", "true");
    } else {
      this.button.removeAttribute("aria-busy");
      this.button.removeAttribute("aria-disabled");
    }
  }

  #submit = async () => {
    if (this.#busy || !this.#orderingEnabled) return;
    const text = this.input.value.trim();
    if (!text) {
      this.input.focus();
      return;
    }

    this.#setBusy(true);
    this.#showError("");
    try {
      const data = await loadData(this.dataset.sourceUrl);
      const decoded = decode(text, data);
      if (decoded.error) {
        this.#showError(decoded.error);
        return;
      }
      const lines = linesFor(decoded.state, data);
      if (lines.error) {
        // Sold out: offer the register-interest link, with the composition in its canonical spelling.
        this.#showError(lines.error, lines.soldOut ? (encode(decoded.state, data) ?? text) : null);
        return;
      }
      await addLines(lines.items);
      this.input.value = "";
      if (this.hasAttribute("data-reload")) {
        window.location.reload();
        return;
      }
      // cart.js has re-rendered the drawer, replacing this element with a fresh one.
      const drawer = document.querySelector("bag-drawer");
      if (!drawer) return;
      if (drawer.isOpen) drawer.querySelector("button[data-bag-close]")?.focus();
      else drawer.open(this.button);
    } catch (failure) {
      this.#showError(failure instanceof Error ? failure.message : CART_ERROR);
    } finally {
      this.#setBusy(false);
    }
  };
}

if (!customElements.get("composition-form")) {
  customElements.define("composition-form", CompositionForm);
}
